/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RiskPresetMatch,
  RiskPresetMatch$inboundSchema,
} from "./riskpresetmatch.js";

/**
 * Suggested enforcement action.
 */
export const SuggestRiskPolicyResultAction = {
  Flag: "flag",
  Warn: "warn",
  Block: "block",
  Quarantine: "quarantine",
} as const;
/**
 * Suggested enforcement action.
 */
export type SuggestRiskPolicyResultAction = ClosedEnum<
  typeof SuggestRiskPolicyResultAction
>;

/**
 * Policy type of the draft.
 */
export const SuggestRiskPolicyResultPolicyType = {
  Standard: "standard",
  PromptBased: "prompt_based",
} as const;
/**
 * Policy type of the draft.
 */
export type SuggestRiskPolicyResultPolicyType = ClosedEnum<
  typeof SuggestRiskPolicyResultPolicyType
>;

/**
 * A policy draft derived from a plain-language description. The fields mirror createRiskPolicy so the dashboard can prefill the create form.
 */
export type SuggestRiskPolicyResult = {
  /**
   * Suggested enforcement action.
   */
  action: SuggestRiskPolicyResultAction;
  /**
   * Other presets that partially matched, best first.
   */
  alternatives: Array<RiskPresetMatch>;
  /**
   * Fit between 0 and 1; 0 for a bespoke draft.
   */
  confidence: number;
  /**
   * Suggested policy name.
   */
  name: string;
  /**
   * Policy type of the draft.
   */
  policyType: SuggestRiskPolicyResultPolicyType;
  /**
   * Matched preset identifier; empty when the draft is a bespoke prompt-based guardrail.
   */
  presetId: string;
  /**
   * Presidio entities for a standard draft that includes presidio.
   */
  presidioEntities: Array<string>;
  /**
   * Judge instruction for a prompt-based draft; empty otherwise.
   */
  prompt: string;
  /**
   * Why this draft was chosen.
   */
  rationale: string;
  /**
   * Suggested CVSS-style severity (0.1-10).
   */
  score: number;
  /**
   * Detector sources for a standard draft; empty for prompt-based drafts.
   */
  sources: Array<string>;
  /**
   * Suggested user-facing message when the policy warns or blocks; empty when none.
   */
  userMessage: string;
};

/** @internal */
export const SuggestRiskPolicyResultAction$inboundSchema: z.ZodMiniEnum<
  typeof SuggestRiskPolicyResultAction
> = z.enum(SuggestRiskPolicyResultAction);

/** @internal */
export const SuggestRiskPolicyResultPolicyType$inboundSchema: z.ZodMiniEnum<
  typeof SuggestRiskPolicyResultPolicyType
> = z.enum(SuggestRiskPolicyResultPolicyType);

/** @internal */
export const SuggestRiskPolicyResult$inboundSchema: z.ZodMiniType<
  SuggestRiskPolicyResult,
  unknown
> = z.pipe(
  z.object({
    action: SuggestRiskPolicyResultAction$inboundSchema,
    alternatives: z.array(RiskPresetMatch$inboundSchema),
    confidence: z.number(),
    name: z.string(),
    policy_type: SuggestRiskPolicyResultPolicyType$inboundSchema,
    preset_id: z.string(),
    presidio_entities: z.array(z.string()),
    prompt: z.string(),
    rationale: z.string(),
    score: z.number(),
    sources: z.array(z.string()),
    user_message: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "policy_type": "policyType",
      "preset_id": "presetId",
      "presidio_entities": "presidioEntities",
      "user_message": "userMessage",
    });
  }),
);

export function suggestRiskPolicyResultFromJSON(
  jsonString: string,
): SafeParseResult<SuggestRiskPolicyResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SuggestRiskPolicyResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SuggestRiskPolicyResult' from JSON`,
  );
}
